import { useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { Box, Button, Divider, Typography, useTheme, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import { activeBucketIdVar, modalVar } from '../../../../providers/apollo/cache'
import { BucketUserRole } from '../../../../graphql/generated'
import useAlerts from '../../hooks/useAlerts'
import useBucketInvitationCreate from '../../hooks/useBucketInvitationCreate'
import CopyLink from '../invite/CopyLinkButtons'
import EmailInput from '../invite/EmailInput'
import useBucket from '../../hooks/useBucket'

export default function InvitationCreateModal() {
  const theme = useTheme()
  const { createAlert } = useAlerts()
  const bucketId = useReactiveVar(activeBucketIdVar)
  const { bucket } = useBucket()
  const activeBucketId = bucketId ?? bucket?.id
  const modal = useReactiveVar(modalVar)
  const [emails, setEmails] = useState<string[]>([])
  const { mutation, loading } = useBucketInvitationCreate()

  const handleClose = () => {
    modalVar(null)
    activeBucketIdVar(null)
    setEmails([])
  }

  const handleSkip = () => {
    handleClose()
  }

  const handleInvite = () => {
    if (!activeBucketId || emails.length === 0) {
      createAlert('Please enter valid email addresses', 'error')
      return
    }

    mutation({
      variables: { bucketId: activeBucketId, emails, userRole: BucketUserRole.Contributor },
      onCompleted: () => {
        createAlert('Invitations have been sent', 'success')
        handleClose()
      },
      onError: (e) => {
        e.graphQLErrors.forEach((error) => {
          createAlert(error.message, 'error', error)
        })
      }
    })
  }

  return (
    <Dialog data-test="invite-people-dialog" open={modal === 'bucketInvite'} onClose={handleClose}>
      <DialogContent
        sx={{
          padding: { xs: '20px', sm: '24px' },
          width: '100%',
          minWidth: {
            xs: '100%',
            sm: 450,
            md: 600
          },
          position: 'relative'
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary'
          }}
          data-test="close-invite-modal"
        >
          <CloseIcon />
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            textAlign: 'center',
            fontWeight: 'bold'
          }}
        >
          Invite Others
        </Typography>
        <CopyLink />
        <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
            or
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>
        <EmailInput fullWidth emails={emails} onEmailsChange={setEmails} />
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: { xs: 1, sm: 2 } }}>
          <Button
            onClick={handleSkip}
            variant="outlined"
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              borderRadius: 10,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: 'transparent'
              }
            }}
          >
            Skip
          </Button>
          <Button
            onClick={handleInvite}
            variant="contained"
            disabled={loading || emails.length === 0}
            sx={{
              borderRadius: 10
            }}
          >
            Invite
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
